import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Text } from '@factorialco/deprecated-design-system'
import groupBy from 'lodash/groupBy'

import i18n from 'modules/core/lib/i18n'

import FlexBetweenRow from './FlexBetweenRow'
import HoverableTooltipRecommendation from './HoverableTooltipRecommendation'

function NamesList({ names }: { names: string[] }) {
  const maxVisible = 5
  const visibleNames = names.slice(0, maxVisible)
  const extraCount = names.length - maxVisible
  return (
    <div>
      <Text.Normal color="grey000White" size="200">
        {visibleNames.join(', ')}
        {extraCount > 0 && `, +${extraCount}`}
      </Text.Normal>
    </div>
  )
}

function TooltipSeparator() {
  return (
    <Box paddingY="s12">
      <div style={{ width: '100%', height: 1, background: '#888' }} />
    </Box>
  )
}

function ShiftsList({
  shifts,
}: {
  shifts: Array<{ name?: string | null; startAt: string; endAt: string; state?: string | null }>
}) {
  const maxVisible = 5
  const shiftsByDay = groupBy(shifts, (shift) => new Date(shift.startAt).toLocaleDateString())
  const days = Object.keys(shiftsByDay)
  let extraCount = 0
  let cut = false

  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }
  const totalShifts = shifts.length
  if (totalShifts > maxVisible) {
    extraCount = totalShifts - maxVisible
  }

  const content: React.ReactNode[] = []
  let shiftsLeft = maxVisible

  days.forEach((dayKey, dayIdx) => {
    if (shiftsLeft <= 0 || cut) return
    const dayShifts = shiftsByDay[dayKey]
    if (!dayShifts || dayShifts.length === 0 || !dayShifts[0]) return
    const start = new Date(dayShifts[0].startAt)
    const startDate = start.toLocaleDateString(undefined, dateOptions)
    const showCount = Math.min(dayShifts.length, shiftsLeft)
    content.push(
      <FlexBetweenRow key={dayKey + '-header'}>
        <Text.Normal color="grey000White" weight="semibold" size="200">
          {startDate}
        </Text.Normal>
        <Text.Normal color="grey000White" weight="semibold" size="200">
          {dayShifts.length}
        </Text.Normal>
      </FlexBetweenRow>
    )
    for (let i = 0; i < showCount; i++) {
      const shift = dayShifts[i]
      if (!shift) continue

      const shiftStart = new Date(shift.startAt)
      const shiftEnd = new Date(shift.endAt)
      const startTime = shiftStart.toLocaleTimeString(undefined, timeOptions)
      const endTime = shiftEnd.toLocaleTimeString(undefined, timeOptions)
      const isDraft = shift.state === 'draft'

      content.push(
        <div key={dayKey + '-shift-' + i}>
          <Text.Normal color="grey000White" size="200">
            {shift.name ? `${shift.name}, ` : ''}
            {startTime}–{endTime}
            {isDraft ? ` ${i18n.t('time_off.approval_recommendation.draft_shift')}` : ''}
          </Text.Normal>
        </div>
      )
    }
    shiftsLeft -= showCount
    if (dayIdx < days.length - 1 && shiftsLeft > 0 && showCount > 0) {
      content.push(<TooltipSeparator key={dayKey + '-sep'} />)
    }
    if (shiftsLeft <= 0) cut = true
  })

  return (
    <div>
      {content}
      {extraCount > 0 && (
        <>
          <TooltipSeparator />
          <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 14, marginTop: 4 }}>
            {i18n.t('time_off.approval_recommendation.shifts_more', { count: extraCount })}
          </div>
        </>
      )}
    </div>
  )
}

const WrapperReviewTooltip = ({
  number,
  names,
  label,
}: {
  number: number
  names: Array<string | { name?: string | null; startAt: string; endAt: string }>
  label: string
}) => {
  if (number === 0) return null

  let title = ''
  let details: React.ReactNode = null
  switch (label) {
    case 'overlap':
    case 'overlaps':
      title = i18n.t('time_off.approval_recommendation.overlaps_tooltip_title')

      details = (
        <>
          <Box paddingY="s4" />
          <FlexBetweenRow>
            <Text.Normal color="grey000White" weight="semibold" size="200">
              {i18n.t('time_off.approval_recommendation.overlaps_tooltip_subtitle')}
            </Text.Normal>
            <Text.Normal color="grey000White" weight="semibold" size="200">
              {number}
            </Text.Normal>
          </FlexBetweenRow>
          <NamesList names={names.filter((n): n is string => typeof n === 'string')} />
        </>
      )
      break
    case 'shift':
    case 'shifts':
      title = i18n.t('time_off.approval_recommendation.scheduled_work_tooltip_title')
      if (names.length > 0 && typeof names[0] === 'object' && 'startAt' in names[0]) {
        details = (
          <>
            <Box paddingY="s4" />
            <ShiftsList
              shifts={names.filter(
                (n): n is { name?: string | null; startAt: string; endAt: string } =>
                  typeof n === 'object' && n !== null && 'startAt' in n
              )}
            />
          </>
        )
      } else {
        details = <NamesList names={names.filter((n): n is string => typeof n === 'string')} />
      }
      break
    default:
      break
  }

  return (
    <HoverableTooltipRecommendation label={label} title={title} count={number} details={details} />
  )
}

export default WrapperReviewTooltip
